import React from 'react';
import { RendererProps, getResultData } from './index';

export const AdminListRenderer: React.FC<RendererProps> = ({ toolCall, darkMode }) => {
  const data = getResultData(toolCall);
  const items = Array.isArray(data) ? data : data?.items || data?.entries || data?.result || [];
  if (!Array.isArray(items)) {
    return (
      <pre className={`text-xs p-3 rounded ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
        {JSON.stringify(data, null, 2)}
      </pre>
    );
  }
  return (
    <div className="space-y-1">
      <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        {items.length} items
      </p>
      <div className="max-h-[60vh] overflow-y-auto space-y-1">
        {items.map((item: any, i: number) => (
          <div
            key={i}
            className={`p-2.5 rounded-lg ${
              darkMode ? 'bg-gray-800' : 'bg-white border border-gray-200'
            }`}
          >
            <div className="flex items-center justify-between">
              <span
                className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}
              >
                {item.username ||
                  item.email ||
                  item.name ||
                  item.title ||
                  item.action ||
                  `Item ${i + 1}`}
              </span>
              {item.role && (
                <span
                  className={`text-xs px-1.5 py-0.5 rounded ${
                    darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {item.role}
                </span>
              )}
              {item.status && (
                <span
                  className={`text-xs px-1.5 py-0.5 rounded ${
                    item.status === 'active' || item.status === 'enabled'
                      ? 'bg-emerald-900/40 text-emerald-300'
                      : 'bg-gray-700 text-gray-400'
                  }`}
                >
                  {item.status}
                </span>
              )}
            </div>
            {item.email && item.username && (
              <p className="text-xs text-gray-500 mt-0.5">{item.email}</p>
            )}
            {item.timestamp && (
              <p className="text-[10px] text-gray-600 mt-0.5">
                {new Date(item.timestamp).toLocaleString()}
              </p>
            )}
            {item.details && typeof item.details === 'object' && (
              <p className="text-xs text-gray-500 mt-0.5">
                {JSON.stringify(item.details).slice(0, 100)}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export const AdminDetailRenderer: React.FC<RendererProps> = ({ toolCall, darkMode }) => {
  const data = getResultData(toolCall);
  if (typeof data === 'string') {
    return (
      <div
        className={`p-3 rounded-lg text-sm whitespace-pre-wrap ${
          darkMode ? 'bg-gray-800 text-gray-200' : 'bg-white text-gray-800'
        }`}
      >
        {data}
      </div>
    );
  }
  return (
    <div
      className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}
    >
      {Object.entries(data)
        .filter(([k]) => !['success', 'cached', 'execution_time', 'error'].includes(k))
        .map(([key, val]) => (
          <div
            key={key}
            className={`flex justify-between py-1.5 ${
              darkMode ? 'border-b border-gray-700/50' : 'border-b border-gray-100'
            } last:border-0`}
          >
            <span className="text-xs text-gray-500">{key.replace(/_/g, ' ')}</span>
            <span
              className={`text-xs font-medium text-right max-w-[60%] truncate ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}
            >
              {typeof val === 'object' ? JSON.stringify(val).slice(0, 60) : String(val)}
            </span>
          </div>
        ))}
    </div>
  );
};

export const AdminSuccessRenderer: React.FC<RendererProps> = ({ toolCall, darkMode }) => {
  const data = getResultData(toolCall);
  const message =
    typeof data === 'string'
      ? data
      : data?.message || data?.detail || JSON.stringify(data).slice(0, 200);
  return (
    <div
      className={`p-4 rounded-lg border ${
        darkMode ? 'bg-emerald-900/20 border-emerald-800/30' : 'bg-emerald-50 border-emerald-200'
      }`}
    >
      <p className={`text-sm ${darkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>✓ {message}</p>
      {typeof data === 'object' && data?.id && (
        <p className="text-xs text-gray-500 mt-1">ID: {data.id}</p>
      )}
    </div>
  );
};

export const AdminStatsRenderer: React.FC<RendererProps> = ({ toolCall, darkMode }) => {
  const data = getResultData(toolCall);
  const entries =
    typeof data === 'object' && data !== null
      ? Object.entries(data).filter(
          ([k]) => !['success', 'cached', 'execution_time', 'error'].includes(k),
        )
      : [];
  return (
    <div className="grid grid-cols-2 gap-2">
      {entries.map(([key, val]) => (
        <div
          key={key}
          className={`p-3 rounded-lg text-center ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}
        >
          <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {typeof val === 'number' ? val.toLocaleString() : String(val).slice(0, 20)}
          </div>
          <div className="text-[10px] text-gray-500">{key.replace(/_/g, ' ')}</div>
        </div>
      ))}
    </div>
  );
};
